"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metaInfo = [{
  label: "RESPONSE",
  value: "WITHIN 24 HOURS"
}, {
  label: "PROJECT SCOPE",
  value: "RESIDENTIAL ASSETS"
}, {
  label: "PARTNERSHIP",
  value: "LONG-TERM VALUE"
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-brand-primary-gold/40 bg-brand-primary-gold/5 p-8">
        <p className="font-serif text-xl text-text-main italic mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-12 px-6 text-xs tracking-[0.2em] uppercase border-border-subtle bg-transparent text-text-main hover:bg-text-main/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-brand-secondary-teal font-default font-medium mb-2 data-[empty=true]:opacity-0 transition-opacity">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} type="text" placeholder="Your full name" className="h-16 px-0 bg-transparent border-0 border-b border-text-muted text-lg font-serif italic placeholder:text-text-muted placeholder:font-serif placeholder:italic focus-visible:border-b-brand-primary-gold focus-visible:ring-0 focus-visible:shadow-none transition-all" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-brand-secondary-teal font-default font-medium mb-2">
              Email Address
            </FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="Your email address" className="h-16 px-0 bg-transparent border-0 border-b border-text-muted text-lg font-serif italic placeholder:text-text-muted placeholder:font-serif placeholder:italic focus-visible:border-b-brand-primary-gold focus-visible:ring-0 focus-visible:shadow-none transition-all" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-brand-secondary-teal font-default font-medium mb-2">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="Your phone number" className="h-16 px-0 bg-transparent border-0 border-b border-text-muted text-lg font-serif italic placeholder:text-text-muted placeholder:font-serif placeholder:italic focus-visible:border-b-brand-primary-gold focus-visible:ring-0 focus-visible:shadow-none transition-all" />
            </FormControl>
            <p className="mt-2 text-[11px] tracking-[0.15em] uppercase text-text-muted">
              We will contact you to discuss your property
            </p>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-brand-secondary-teal font-default font-medium mb-2">
              Preferred Contact Date
            </FormLabel>
            <FormControl>
              <Input {...field} type="date" className="h-16 px-0 bg-transparent border-0 border-b border-text-muted text-lg font-serif italic text-text-main focus-visible:border-b-brand-primary-gold focus-visible:ring-0 focus-visible:shadow-none transition-all" />
            </FormControl>
            <p className="mt-2 text-[11px] tracking-[0.15em] uppercase text-text-muted">
              Select a date that suits your schedule
            </p>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase text-brand-secondary-teal font-default font-medium mb-2">
              Property Details
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Briefly describe the property or your investment goals" rows={3} className="px-0 py-4 bg-transparent border-0 border-b border-text-muted text-lg font-serif italic placeholder:text-text-muted placeholder:font-serif placeholder:italic focus-visible:border-b-brand-primary-gold focus-visible:ring-0 focus-visible:shadow-none transition-all resize-none min-h-[80px]" />
            </FormControl>
            <FormMessage className="mt-2 text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-[60px] bg-brand-secondary-teal text-white hover:bg-brand-primary-gold text-sm tracking-[0.25em] uppercase font-default font-semibold transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-1 size-4" />
            </>}
        </Button>
        <p className="mt-6 font-serif italic text-sm text-text-muted">
          Confidentiality is our priority. All inquiries are reviewed personally.
        </p>
      </div>
    </Form>;
}
export default function Conversation() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4679" id="start-a-conversation" className="bg-background text-text-main py-24 md:py-40 lg:py-[200px] px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.3em] uppercase text-brand-primary-gold font-default font-medium mb-12">
              — 06 / Start a Conversation
            </p>

            <h2 className="font-default font-bold text-text-main text-5xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight">
              Begin
              <br />
              <span className="font-serif italic font-normal">thoughtfully.</span>
            </h2>

            <p className="mt-12 lg:mt-[60px] font-serif text-lg md:text-xl text-text-muted leading-relaxed max-w-[380px]">
              We prioritize integrity and long-term value. Every partnership begins with a private, confidential discussion.
            </p>

            <div className="mt-16 space-y-0">
              {metaInfo.map((item, i) => <div key={i} data-index={i} className="border-t border-border-subtle py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-[11px] tracking-[0.25em] uppercase text-text-muted font-default">
                    {item.label}
                  </span>
                  <span className="text-[11px] tracking-[0.25em] uppercase text-text-main font-default font-medium">
                    {item.value}
                  </span>
                </div>)}
              <div className="border-t border-border-subtle" />
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="conversation_form_home" formId="conversation_form_home" sectionName="start-a-conversation" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            message: {
              required: false
            }
          }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. We will respond within 24 hours." errorMessage="Unable to submit your inquiry. Please try again or contact us directly.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}